import get from "lodash-es/get";

import { FTPS, HTTPS, LICENSE, OTA } from "constants/settingsTLSTypes";
import {
  ACTION_LOG,
  ACTION_LOG_LABEL,
  FTP,
  FTP_LABEL,
  OKTA,
  OKTA_LABEL,
  SMTP,
  SMTP_LABEL,
} from "constants/repoSettings";

export const GetRolesMapper = (data) =>
  data.system.user_settings.groups
    .map((group) => ({
      groupName: group.name,
      groupId: group.id,
    }))
    .sort((a, b) => a.groupId - b.groupId);

export const GetRoleMapper = (data) =>
  data.system.user_settings.group_permissions;

export const GetUsersMapper = (data) => {
  const currentUser = {};
  const users = [];
  data.system.user_settings.users.forEach((user) => {
    const userInfo = {
      firstName: user.first_name,
      isCurrent: user.is_current,
      isSuperuser: user.is_superuser,
      lastName: user.last_name,
      userName: user.username,
      groupName: user.is_superuser ? "Superuser" : user.groups[0]?.name,
      userId: user.id,
      email: user.email,
    };
    if (user.is_current) Object.assign(currentUser, userInfo);
    else users.push(userInfo);
  });
  users.sort((a, b) => a.userId - b.userId);

  return [currentUser, users];
};

export const GetTlsSettingsMapper = (data) => {
  const certificates = get(data, "system.certificates", []);
  return [HTTPS, FTPS, LICENSE, OTA].map((name, index) =>
    _getCertificateInfo(certificates, name, index),
  );
};

export const ReadServicesMapper = (data) => {
  const services = data.system.services;

  return [_getAntivirusServiceInfo(services), _getBillingServiceInfo(services)];
};

const _getCertificateInfo = (data, certType, index) => {
  const cert = data.find((cert) => cert.type === certType);
  const meta = get(cert, "meta", []);
  const isAnyExpired = meta.some((certificate) => certificate.Expired);
  return {
    id: index,
    name: certType,
    isAnyExpired,
    hasFile: Boolean(meta.length),
    info: meta,
  };
};

export const GetRepoInfoMapper = (data) => {
  return get(data, "system.server_info", {});
};

export const systemEventsMapper = (data) => {
  const systemEvents = get(data, "system.system_events", []);
  return systemEvents.map((item) => ({
    timestamp: get(item, "timestamp", ""),
    template: get(item, "template", ""),
    params: get(item, "params", {}),
    eventType: get(item, "event_type", null),
    resourceMetadata: get(item, "resource_metadata", {}),
    id: get(item, "id_sys_event", null),
  }));
};

const _getAntivirusServiceInfo = (data) => {
  const service = data.find((item) => item.type === "cvdupdate") || {};
  return {
    name: get(service, "name", "Antivirus"),
    enabled: get(service, "enabled", false),
    status: get(service, "status", "N/A"),
    type: get(service, "type", "cvdupdate"),
    statusMessage: get(service, "status_message", "N/A"),
  };
};

const _getBillingServiceInfo = (data) => {
  const service = data.find((item) => item.type === "pm_proxy") || {};
  return {
    name: get(service, "name", "Billing proxy"),
    enabled: get(service, "enabled", false),
    status: get(service, "status", "N/A"),
    type: get(service, "type", "pm_proxy"),
    statusMessage: get(service, "status_message", "N/A"),
    properties: get(service, "properties", {}),
  };
};

export const readSettingsMapper = (data) => {
  const okta = get(data, "system.server_settings.okta", {});
  const smtp = get(data, "system.server_settings.smtp", {});
  const actionLog = get(data, "system.server_settings.action_log", {});
  const ftp = get(data, "system.server_settings.proftpd", {});

  return [
    _getOktaSettingsInfo(okta),
    _getSmtpSettingsInfo(smtp),
    _getActionLogSettingsInfo(actionLog),
    _getFtpSettingsInfo(ftp),
  ];
};

const _getOktaSettingsInfo = (data) => {
  return {
    type: OKTA,
    name: OKTA_LABEL,
    data: {
      app_id: _getSettingsValueAsString(data, "app_id"),
      app_secret: _getSettingsValueAsString(data, "app_secret"),
      auth_server_audience: _getSettingsValueAsString(
        data,
        "auth_server_audience",
      ),
      enabled: get(data, "enabled", false),
      auth_server_id: _getSettingsValueAsString(data, "auth_server_id"),
      org_domain: _getSettingsValueAsString(data, "org_domain"),
      redirect_uri: _getSettingsValueAsString(data, "redirect_uri"),
    },
  };
};

const _getSmtpSettingsInfo = (data) => {
  return {
    type: SMTP,
    name: SMTP_LABEL,
    data: {
      from_addr: get(data, "from_addr"),
      smtp_login: get(data, "smtp_login"),
      smtp_server: get(data, "smtp_server"),
      smtp_port: _getSettingsValueAsInteger(data, "smtp_port"),
      smtp_pass: get(data, "smtp_pass"),
      to_addr: get(data, "to_addr"),
    },
  };
};

const _getActionLogSettingsInfo = (data) => {
  return {
    type: ACTION_LOG,
    name: ACTION_LOG_LABEL,
    data: {
      rotation_years: get(data, "rotation_years"),
    },
  };
};

const _getFtpSettingsInfo = (data) => {
  return {
    type: FTP,
    name: FTP_LABEL,
    data: {
      masquerade_address: get(data, "masquerade_address"),
    },
  };
};

const _getSettingsValueAsString = (data, key) => {
  const value = get(data, key, "");
  if (value === null) {
    return "";
  }
  return value;
};

const _getSettingsValueAsInteger = (data, key) => {
  const value = get(data, key, 0);
  if (value === null) {
    return 0;
  }
  return value;
};
